/**
 * GeometryFactory - Converts GeometryDataTyped to ODA DWG entities.
 *
 * Used by parametric assets to create native DWG geometry from
 * simplified geometry definitions.
 */

import type { GeometryDataTyped, Point3 } from "@/types/blocks.types";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { openAs, getCurrentSpaceId } from "../core/oda";
import { OpenMode } from "../core/constants";
import { calculateArcFrom3Points } from "../core/geometry-math";

/**
 * Offset for positioning geometry in world coordinates.
 */
export interface GeometryOffset {
  x: number;
  y: number;
  z: number;
}

/**
 * Result of creating geometry entities.
 */
export interface GeometryFactoryResult {
  entityIds: DrawingModule.OdDbObjectId[];
}

/**
 * Factory for creating ODA DWG entities from GeometryDataTyped arrays.
 */
export class GeometryFactory {
  /**
   * Create ODA entities from GeometryDataTyped array.
   *
   * @param viewer The drawing viewer instance
   * @param geometries Array of geometry definitions
   * @param offset Offset to apply to all geometry (typically canvas center)
   * @returns Array of created entity IDs
   */
  static createEntities(
    viewer: DRDrawingViewer,
    geometries: GeometryDataTyped[],
    offset: GeometryOffset = { x: 0, y: 0, z: 0 },
  ): GeometryFactoryResult {
    const module = viewer.module;
    const db = viewer.database;

    if (!db) {
      console.error("[GeometryFactory] No database available");
      return { entityIds: [] };
    }

    const entityIds: DrawingModule.OdDbObjectId[] = [];

    for (const geometry of geometries) {
      try {
        let entityId: DrawingModule.OdDbObjectId | null = null;

        switch (geometry.type) {
          case "polyline":
            entityId = this.createPolyline(module, db, geometry.points, offset);
            break;
          case "circle":
            entityId = this.createCircle(
              module,
              db,
              geometry.center,
              geometry.radius,
            );
            break;
          case "ellipse":
            entityId = this.createEllipse(
              module,
              db,
              geometry.center,
              geometry.majorPoint,
              geometry.minorPoint,
            );
            break;
          case "circle-arc":
            entityId = this.createArc(
              module,
              db,
              geometry.start,
              geometry.middle,
              geometry.end,
            );
            break;
          case "text":
            entityId = this.createText(
              module,
              db,
              geometry.refpoint,
              geometry.message,
            );
            break;
          case "polygon":
            // Polygon is a closed polyline
            entityId = this.createPolyline(
              module,
              db,
              geometry.corners,
              offset,
              true,
            );
            break;
          default:
            console.warn(
              "[GeometryFactory] Unknown geometry type:",
              (geometry as any).type,
            );
        }

        if (entityId) {
          entityIds.push(entityId);
        }
      } catch (error) {
        console.error("[GeometryFactory] Failed to create geometry:", error);
      }
    }

    return { entityIds };
  }

  /**
   * Create an OdDbPolyline from points.
   * Note: Uses OdDbPolyline instead of OdDbLine per CLAUDE.md guidance.
   */
  private static createPolyline(
    module: DrawingModuleType,
    db: DrawingModule.OdDbDatabase,
    points: Point3[],
    _offset: GeometryOffset,
    closed: boolean = false,
  ): DrawingModule.OdDbObjectId | null {
    if (points.length < 2) {
      return null;
    }

    try {
      const polyline = module.OdDbPolyline.createObject();
      polyline.setDatabaseDefaults(db, true);

      for (let i = 0; i < points.length; i++) {
        const pt = points[i];
        // Points from assets are already offset-adjusted via the build() function
        // We don't need to apply offset here as assets already include it
        const point = new module.OdGePoint2d(pt[0], pt[1]);
        polyline.addVertexAt(i, point, 0, 0, 0, 0);
        deleteObj(point);
      }

      if (closed) {
        polyline.setClosed(true);
      }

      // Append to current space
      const currentSpaceId = getCurrentSpaceId(db);
      const currentSpace = openAs(
        currentSpaceId,
        module.OdDbBlockTableRecord,
        OpenMode.kForWrite,
      );
      const entityId = currentSpace.appendOdDbEntity(polyline);

      deleteObj(currentSpace);
      deleteObj(polyline);

      return entityId;
    } catch (error) {
      console.error("[GeometryFactory] Failed to create polyline:", error);
      return null;
    }
  }

  /**
   * Create an OdDbCircle.
   */
  private static createCircle(
    module: DrawingModuleType,
    db: DrawingModule.OdDbDatabase,
    center: Point3,
    radius: number,
  ): DrawingModule.OdDbObjectId | null {
    try {
      const circle = module.OdDbCircle.createObject();
      circle.setDatabaseDefaults(db, true);

      // Center is already offset-adjusted from asset build()
      const centerPoint = new module.OdGePoint3d(
        center[0],
        center[1],
        center[2],
      );
      circle.setCenter(centerPoint);
      circle.setRadius(radius);

      deleteObj(centerPoint);

      // Append to current space
      const currentSpaceId = getCurrentSpaceId(db);
      const currentSpace = openAs(
        currentSpaceId,
        module.OdDbBlockTableRecord,
        OpenMode.kForWrite,
      );
      const entityId = currentSpace.appendOdDbEntity(circle);

      deleteObj(currentSpace);
      deleteObj(circle);

      return entityId;
    } catch (error) {
      console.error("[GeometryFactory] Failed to create circle:", error);
      return null;
    }
  }

  /**
   * Create an OdDbEllipse.
   */
  private static createEllipse(
    module: DrawingModuleType,
    db: DrawingModule.OdDbDatabase,
    center: Point3,
    majorPoint: Point3,
    minorPoint: Point3,
  ): DrawingModule.OdDbObjectId | null {
    try {
      const ellipse = module.OdDbEllipse.createObject();
      ellipse.setDatabaseDefaults(db, true);

      // Convert points - they are already offset-adjusted from asset build()
      const centerPt = new module.OdGePoint3d(center[0], center[1], center[2]);
      const normal = new module.OdGeVector3d(0, 0, 1);

      // Major axis vector (from center to major point)
      const majorAxisVec = new module.OdGeVector3d(
        majorPoint[0] - center[0],
        majorPoint[1] - center[1],
        majorPoint[2] - center[2],
      );

      // Calculate radius ratio from minor point
      const majorRadius = Math.sqrt(
        majorAxisVec.x * majorAxisVec.x +
          majorAxisVec.y * majorAxisVec.y +
          majorAxisVec.z * majorAxisVec.z,
      );
      const minorDist = Math.sqrt(
        Math.pow(minorPoint[0] - center[0], 2) +
          Math.pow(minorPoint[1] - center[1], 2) +
          Math.pow(minorPoint[2] - center[2], 2),
      );
      const radiusRatio = majorRadius > 0 ? minorDist / majorRadius : 1;

      ellipse.set(centerPt, normal, majorAxisVec, radiusRatio, 0, Math.PI * 2);

      deleteObj(centerPt);
      deleteObj(normal);
      deleteObj(majorAxisVec);

      // Append to current space
      const currentSpaceId = getCurrentSpaceId(db);
      const currentSpace = openAs(
        currentSpaceId,
        module.OdDbBlockTableRecord,
        OpenMode.kForWrite,
      );
      const entityId = currentSpace.appendOdDbEntity(ellipse);

      deleteObj(currentSpace);
      deleteObj(ellipse);

      return entityId;
    } catch (error) {
      console.error("[GeometryFactory] Failed to create ellipse:", error);
      return null;
    }
  }

  /**
   * Create an OdDbArc from 3 points.
   */
  private static createArc(
    module: DrawingModuleType,
    db: DrawingModule.OdDbDatabase,
    start: Point3,
    middle: Point3,
    end: Point3,
  ): DrawingModule.OdDbObjectId | null {
    try {
      // Calculate arc parameters from 3 points
      const arcParams = calculateArcFrom3Points(start, middle, end);
      if (!arcParams) {
        console.warn("[GeometryFactory] Could not calculate arc from 3 points");
        return null;
      }

      const arc = module.OdDbArc.createObject();
      arc.setDatabaseDefaults(db, true);

      const center = new module.OdGePoint3d(
        arcParams.center.x,
        arcParams.center.y,
        0,
      );
      arc.setCenter(center);
      arc.setRadius(arcParams.radius);
      arc.setStartAngle(arcParams.startAngle);
      arc.setEndAngle(arcParams.endAngle);

      deleteObj(center);

      // Append to current space
      const currentSpaceId = getCurrentSpaceId(db);
      const currentSpace = openAs(
        currentSpaceId,
        module.OdDbBlockTableRecord,
        OpenMode.kForWrite,
      );
      const entityId = currentSpace.appendOdDbEntity(arc);

      deleteObj(currentSpace);
      deleteObj(arc);

      return entityId;
    } catch (error) {
      console.error("[GeometryFactory] Failed to create arc:", error);
      return null;
    }
  }

  /**
   * Create an OdDbMText entity.
   * Uses MText instead of Text for better multiline support.
   */
  private static createText(
    module: DrawingModuleType,
    db: DrawingModule.OdDbDatabase,
    refpoint: Point3,
    message: string,
  ): DrawingModule.OdDbObjectId | null {
    try {
      const mtext = module.OdDbMText.createObject();
      mtext.setDatabaseDefaults(db, true);

      // Position is already offset-adjusted from asset build()
      const location = new module.OdGePoint3d(
        refpoint[0],
        refpoint[1],
        refpoint[2],
      );
      mtext.setLocation(location);

      const textStr = new module.OdString(message);
      mtext.setContents(textStr);

      // Default text height
      mtext.setTextHeight(2.5);

      deleteObj(location);
      deleteObj(textStr);

      // Append to current space
      const currentSpaceId = getCurrentSpaceId(db);
      const currentSpace = openAs(
        currentSpaceId,
        module.OdDbBlockTableRecord,
        OpenMode.kForWrite,
      );
      const entityId = currentSpace.appendOdDbEntity(mtext);

      deleteObj(currentSpace);
      deleteObj(mtext);

      return entityId;
    } catch (error) {
      console.error("[GeometryFactory] Failed to create text:", error);
      return null;
    }
  }
}
